import React, { useState } from 'react';
import {
    instagramNewIcon,
    youtubeNewIcon,
    reelsIconInstagram,
    postsIconInstagram,
    reelsContentIcon,
    Story,
    Shorts
} from '../../../components/assets/icons';
import FallbackImg from '../../../resources/images/content_fallback.png';
import Utils from '../../../common/utils';
import { Image, Video, PrimaryCta } from '../../../components';
import { InstagramEmbed, XEmbed, YouTubeEmbed } from "react-social-media-embed";
import Modal from "react-modal";
import Icon from "../../../components/core_component/icon";

// Platform ID mapping
const PLATFORM_ID_MAP = {
    btll: 1, // Instagram
    yt: 2, // YouTube
    1: 1,
    2: 2,
    3: 3,
    4: 4,
    5: 5,
};

export const normalizePlatformId = (platformId) => PLATFORM_ID_MAP[platformId] || platformId;

// Get platform info
const getPlatformInfo = (platformId) => {
    const normalizedId = normalizePlatformId(platformId);
    const platforms = {
        1: {
            name: 'Instagram',
            icon: instagramNewIcon,
            isComponent: true,
            color: '#E4405F',
            gradient:
                'linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)',
        },
        2: {
            name: 'YouTube',
            icon: youtubeNewIcon,
            isComponent: true,
            color: '#FF0000',
            gradient: 'linear-gradient(45deg, #FF0000, #CC0000)',
        },
    };
    return (
        platforms[normalizedId] || {
            name: 'Unknown',
            icon: null,
            isComponent: false,
            color: '#6B7280',
            gradient: '#6B7280',
        }
    );
};

// Format large numbers
const formatNumber = (num) => {
    if (!num) return '0';
    if (num >= 1000000) {
        return `${(num / 1000000).toFixed(1)}M`;
    }
    if (num >= 1000) {
        return `${(num / 1000).toFixed(1)}K`;
    }
    return num.toString();
};

const UGCContentCard = ({ item }) => {
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [currentItem, setCurrentItem] = useState(null);

    // Map UGC data to ContentCard expectations
    // ContentSection currently maps: influencerUsername -> owner_name, influencerDp -> owner_pic
    // Or we can use the props directly if they are passed.
    // The item prop here is the flattened structure created in ContentSection + original item in `item.item`

    const ownerName = item.influencerUsername || item.name || 'Creator';
    const ownerPic = item.influencerDp || item.profilePic || FallbackImg;
    const platformId = item.platform_id; // Might need mapping if string
    const platformInfo = getPlatformInfo(platformId);

    // UGC content usually doesn't have these metrics populated in the same way, but let's try
    // The mapped data in ContentSection doesn't map these fields yet, I need to fix mapping or fallback
    const views = item.views || 0;
    const likes = item.likes || 0;
    const comments = item.comments || 0;
    const er = 0; // consistent with UGC usually being new/raw

    const handleCardClick = () => {
        setCurrentItem(item);
        setModalIsOpen(true);
    };

    const closeModal = (e) => {
        if (e) e.stopPropagation();
        setModalIsOpen(false);
        setCurrentItem(null);
    };

    // Copied from InfluencerContentCard logic
    const handleDownload = (media_id, isVideo, assignment_id) => {
        const mediaUrl = Utils.constructContentMediaUrl(assignment_id, media_id);
        const a = document.createElement("a");
        a.href = mediaUrl;
        a.download = `downloaded_${isVideo ? "video" : "image"}.${media_id
            .split(".")
            .pop()}`;
        a.style.display = "none";

        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    };

    function socialMedialEmbed(item) {
        // Match logic from InfluencerContentCard.js
        const platform = Utils.socialMediaPlatform(item?.content_type_id);
        const mediaType = item?.imageSource?.split(".").pop() === "mp4";

        return (
            <>
                {item?.url && (
                    <div className="embed-container">
                        {platform === "Instagram" && (
                            <InstagramEmbed url={item?.url} width={"100%"} />
                        )}
                        {platform === "youtube" && (
                            <YouTubeEmbed url={item?.url} width={"100%"} height={620} />
                        )}
                        {platform === "twitter" && (
                            <XEmbed url={item?.url} width={"100%"} />
                        )}
                    </div>
                )}
                {item?.imageSource && mediaType && !item.url && (
                    <div className="download-content">
                        <Video
                            source={Utils.constructContentMediaUrl(
                                item?.assignmentId,
                                item?.imageSource
                            )}
                            width={"100%"}
                            height={420}
                            className="content-media"
                        />
                        <PrimaryCta
                            fifth
                            onClick={() =>
                                handleDownload(
                                    item?.imageSource,
                                    true,
                                    item?.assignmentId
                                )
                            }
                            fontSize={"font_size_large"}
                            text={"Download"}
                            className="blue-button"
                        />
                    </div>
                )}
                {item?.imageSource && !mediaType && !item.url && (
                    <div className="download-content">
                        <Image
                            source={Utils.constructContentMediaUrl(
                                item?.assignmentId,
                                item?.imageSource
                            )}
                            width={"100%"}
                            height={420}
                            className="content-media"
                        />
                        <PrimaryCta
                            fifth
                            onClick={() =>
                                handleDownload(
                                    item?.imageSource,
                                    false,
                                    item?.assignmentId
                                )
                            }
                            fontSize={"font_size_large"}
                            text={"Download"}
                            className="blue-button"
                        />
                    </div>
                )}
            </>
        );
    }

    // Thumbnail Logic
    const getThumbnailUrl = () => {
        // If raw media
        if (item.imageSource) {
            return Utils.constructContentMediaUrl(item.assignmentId, item.imageSource);
        }
        // Fallback for youtube etc handled in ContentCard, but here we prioritize raw media
        return null;
    };
    const thumbnailUrl = getThumbnailUrl();


    return (
        <>
            <div className="content-grid-card" onClick={handleCardClick} style={{ cursor: 'pointer' }}>
                {/* Card Header */}
                <div className="content-card-header">
                    <div className="content-creator-info">
                        <Image
                            source={
                                item.profile_pic || item.ownerPic
                                    ? Utils.generateInfluencerDpUrl(
                                        item.influencerId || item.influencer_id,
                                        item.profile_pic || item.ownerPic
                                    )
                                    : FallbackImg
                            }
                            fallBack={FallbackImg}
                            className="content-creator-avatar"
                            style={{ width: '36px', height: '36px', objectFit: 'cover', borderRadius: '50%' }}
                        />
                        <div style={{ overflow: 'hidden' }}>
                            <span
                                className="content-creator-name"
                                style={{
                                    display: 'block',
                                    fontSize: '13px',
                                    fontWeight: 600,
                                    color: '#111827',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    maxWidth: '160px',
                                }}
                            >
                                {ownerName}
                            </span>
                        </div>
                    </div>

                </div>

                {/* Card Image/Thumbnail */}
                <div
                    className="content-card-image"
                >
                        {thumbnailUrl ? (
                            (thumbnailUrl.endsWith('.mp4') || (item.videoUrl && item.videoUrl.endsWith('.mp4'))) ? (
                                <video
                                    src={thumbnailUrl}
                                    muted
                                    onMouseOver={event => event.target.play()}
                                    onMouseOut={event => event.target.pause()}
                                />
                            ) : (
                                <Image
                                    source={thumbnailUrl}
                                    fallBack={FallbackImg}
                                />
                            )
                        ) : (
                            <Image
                                source={FallbackImg}
                            />
                        )}

                    {/* Play Button Overlay */}
                    <div
                        style={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: '56px',
                            height: '56px',
                            borderRadius: '50%',
                            background: 'rgba(0,0,0,0.6)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            // Always show play button as indication it's clickable/media
                        }}
                        className="play-overlay"
                    >
                        <span style={{ color: 'white', fontSize: '24px', marginLeft: '4px' }}>▶</span>
                    </div>
                </div>

                {/* Card Footer */}
                <div className="content-card-footer" style={{ padding: '14px' }}>
                    {/* Caption Preview */}
                    {item.caption && (
                        <div className="content-card-caption-clip" title={item.caption} style={{ marginBottom: '10px' }}>
                            {item.caption}
                        </div>
                    )}

                    {/* Hashtags */}
                    {item.hash_tags && (
                        <div
                            style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: '4px',
                            }}
                        >
                            {item.hash_tags
                                .split(',')
                                .slice(0, 3)
                                .map((tag, idx) => (
                                    <span
                                        key={idx}
                                        style={{
                                            fontSize: '11px',
                                            color: '#6b7280',
                                            background: '#f3f4f6',
                                            padding: '2px 8px',
                                            borderRadius: '10px',
                                            fontWeight: 500,
                                        }}
                                    >
                                        {tag.trim()}
                                    </span>
                                ))}
                            {item.hash_tags.split(',').length > 3 && (
                                <span
                                    style={{
                                        fontSize: '11px',
                                        color: '#9ca3af',
                                        padding: '2px 8px',
                                    }}
                                >
                                    +{item.hash_tags.split(',').length - 3} more
                                </span>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Modal */}
            <Modal
                isOpen={modalIsOpen}
                onRequestClose={closeModal}
                style={{
                    content: {
                        top: "50%",
                        left: "50%",
                        right: "auto",
                        bottom: "auto",
                        marginRight: "-50%",
                        transform: "translate(-50%, -50%)",
                        background: "var(--general-secondary-color)",
                        textAlign: "center",
                        zIndex: "5",
                        display: "flex",
                        flexDirection: "column",
                        maxHeight: "90vh",
                        borderRadius: 12,
                        overflowY: "auto",
                        width: "400px",
                        minHeight: "500px",
                    },
                    overlay: {
                        background: "rgba(0, 0, 0, 0.95)",
                        zIndex: 10,
                    },
                }}
            >
                <div onClick={closeModal} style={{ position: "absolute", right: 10, top: 10, cursor: "pointer" }}>
                    <Icon iconName="crossIcon" />
                </div>
                {currentItem && socialMedialEmbed(currentItem)}
            </Modal>
        </>
    );
};

export default UGCContentCard;
